<script setup>
import { ref, computed, watch, provide, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import MacroForm from './MacroForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { MACRO_ACTION_TYPES } from './constants';
import { useAlert } from 'dashboard/composables';
import actionQueryGenerator from 'dashboard/helper/actionQueryGenerator.js';
import { useMacros } from 'dashboard/composables/useMacros';
import { useAdmin } from 'dashboard/composables/useAdmin';

const store = useStore();

const route = useRoute();
const router = useRouter();

const { t } = useI18n();

const { getMacroDropdownValues } = useMacros();
const { isAdmin } = useAdmin();

const macro = ref(null);
const mode = ref('CREATE');
const isLoading = ref(false);
const loadError = ref(false);
let loadVersion = 0;

const macroActionTypes = computed(() => {
  return MACRO_ACTION_TYPES.map(type => ({
    ...type,
    label: t(`MACROS.ACTIONS.${type.label}`),
  }));
});

provide('macroActionTypes', macroActionTypes);

const macroId = computed(() => route.params.macroId);
const isPublicMacroReadOnly = computed(
  () => macro.value?.visibility === 'global' && !isAdmin.value
);

const fetchDropdownData = () =>
  Promise.all([
    store.dispatch('agents/get'),
    store.dispatch('teams/get'),
    store.dispatch('labels/get'),
  ]);

const formatMacro = macroData => {
  const formattedActions = macroData.actions.map(action => {
    let actionParams = [];
    if (action.action_params.length) {
      const inputType = macroActionTypes.value.find(
        item => item.key === action.action_name
      ).inputType;
      if (inputType === 'multi_select' || inputType === 'search_select') {
        actionParams = getMacroDropdownValues(action.action_name).filter(item =>
          [...action.action_params].includes(item.id)
        );
      } else if (inputType === 'team_message') {
        actionParams = {
          team_ids: getMacroDropdownValues(action.action_name).filter(item =>
            [...action.action_params[0].team_ids].includes(item.id)
          ),
          message: action.action_params[0].message,
        };
      } else actionParams = [...action.action_params];
    }
    return {
      ...action,
      action_params: actionParams,
    };
  });
  return {
    ...macroData,
    actions: formattedActions,
  };
};

const initNewMacro = () => {
  mode.value = 'CREATE';
  macro.value = {
    name: '',
    actions: [
      {
        action_name: 'assign_team',
        action_params: [],
      },
    ],
    visibility: isAdmin.value ? 'global' : 'personal',
  };
};

const loadMacro = async () => {
  loadVersion += 1;
  const version = loadVersion;
  const id = macroId.value;
  macro.value = null;
  loadError.value = false;
  isLoading.value = true;
  try {
    await fetchDropdownData();
    if (version !== loadVersion) return;
    if (id) {
      const data = await store.dispatch('macros/getSingleMacro', id);
      if (version !== loadVersion) return;
      macro.value = formatMacro(data);
      mode.value = 'EDIT';
    } else {
      initNewMacro();
    }
  } catch (error) {
    if (version === loadVersion) loadError.value = true;
  } finally {
    if (version === loadVersion) isLoading.value = false;
  }
};

watch([() => route.params.accountId, () => route.params.macroId], loadMacro, {
  immediate: true,
});
onBeforeUnmount(() => {
  loadVersion += 1;
});

const saveMacro = async macroData => {
  if (isPublicMacroReadOnly.value) return;

  try {
    const action = mode.value === 'EDIT' ? 'macros/update' : 'macros/create';
    const successMessage =
      mode.value === 'EDIT'
        ? t('MACROS.EDIT.API.SUCCESS_MESSAGE')
        : t('MACROS.ADD.API.SUCCESS_MESSAGE');
    let serializedMacro = JSON.parse(JSON.stringify(macroData));
    serializedMacro.actions = actionQueryGenerator(serializedMacro.actions);
    await store.dispatch(action, serializedMacro);
    useAlert(successMessage);
    router.push({ name: 'macros_wrapper' });
  } catch (error) {
    useAlert(t('MACROS.ERROR'));
  }
};
</script>

<template>
  <div
    class="flex flex-col gap-6 mb-8 max-w-7xl mx-auto h-full w-full !px-3 sm:!px-6"
  >
    <woot-loading-state
      v-if="isLoading"
      :message="t('MACROS.EDITOR.LOADING')"
    />
    <div
      v-else-if="loadError"
      role="alert"
      class="flex flex-col items-start gap-3 p-4 rounded-xl border border-n-weak"
    >
      <p class="m-0 text-n-slate-11">{{ t('MACROS.EDITOR.LOAD_ERROR') }}</p>
      <Button :label="t('MACROS.EDITOR.RETRY')" @click="loadMacro" />
    </div>
    <MacroForm
      v-else-if="macro"
      :macro-data="macro"
      :can-manage-public-macros="isAdmin"
      :read-only="isPublicMacroReadOnly"
      @update:macro-data="macro = $event"
      @submit="saveMacro"
    />
  </div>
</template>
